import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import GoogleMapReact from 'google-map-react';
import { LocationOn } from '@mui/icons-material';
import { GOOGLE_MAPS_API_KEY } from '../../config/env';

const Marker = () => <LocationOn sx={{ color: 'error.main', transform: 'translate(-50%, -100%)' }} />;

const MapComponent = ({ 
  address, 
  mapCenter, 
  setMapCenter, 
  onAddressChange, 
  onLocationSelect,
  addressInputProps = {} 
}) => {
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    // Script zaten yüklüyse tekrar yükleme
    if (window.google || scriptLoadedRef.current) {
      initAutocomplete();
      return;
    }

    scriptLoadedRef.current = true;
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.addEventListener('load', () => {
      initAutocomplete();
    });

    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const initAutocomplete = () => {
    const input = document.getElementById('address-input');
    if (!input || !window.google || !window.google.maps || !window.google.maps.places) {
      return;
    }

    try {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(input, {
        types: ['address']
      });

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (place.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          
          setMapCenter({ lat, lng });
          onLocationSelect({ 
            latitude: lat.toString(),
            longitude: lng.toString(),
            address: place.formatted_address 
          });
        }
      });
    } catch (error) {
      console.error('Error initializing autocomplete:', error);
    }
  };

  const handleApiLoaded = ({ map, maps }) => {
    mapRef.current = map;
  };

  const handleMapClick = (e) => {
    const { lat, lng } = e;
    setMapCenter({ lat, lng });

    if (!window.google || !window.google.maps) {
      onLocationSelect({ 
        latitude: lat.toString(),
        longitude: lng.toString()
      });
      return;
    }

    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat, lng } },
        (results, status) => {
          if (status === 'OK' && results[0]) {
            onLocationSelect({
              latitude: lat.toString(),
              longitude: lng.toString(),
              address: results[0].formatted_address
            });
          } else {
            onLocationSelect({ 
              latitude: lat.toString(),
              longitude: lng.toString()
            });
          }
        }
      );
    } catch (error) {
      console.error('Error geocoding location:', error);
      onLocationSelect({ 
        latitude: lat.toString(),
        longitude: lng.toString()
      });
    }
  };

  return (
    <>
      {addressInputProps.render({ 
        value: address, 
        onChange: onAddressChange,
        inputProps: { id: 'address-input' }
      })}

      <Box height={300} width="100%" mb={2}>
        <GoogleMapReact
          bootstrapURLKeys={{ 
            key: GOOGLE_MAPS_API_KEY,
            libraries: ['places']
          }}
          defaultCenter={mapCenter}
          center={mapCenter}
          defaultZoom={13}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={handleApiLoaded}
          onClick={handleMapClick}
        >
          <Marker lat={mapCenter.lat} lng={mapCenter.lng} />
        </GoogleMapReact>
      </Box>
    </>
  );
};

export default MapComponent; 